import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import keys from "configs/constants";
import InfoDialog from "./InfoDialog";
import VersionText from "./VersionText";
import { PRODUCT_NAME, VERSION_SEPARATOR, versionParts } from "./infoDocuments";
import "./infoMenu.css";

const COPIED_FEEDBACK_MS = 1600;
const MENU_ID = "sidebar-info-menu";

/**
 * Bottom of the sidebar (AB#49545): the software version, and the
 * "Documents" menu. The menu opens beside the sidebar rather than over it, so
 * the navigation stays visible; its last item, "Info, privacy, terms and
 * conditions", opens the info dialog in place.
 */
const SidebarInfoMenu = ({ t, isSuperAdmin = false, isCompact = false }) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isCopied, setCopied] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [focusFirstItem, setFocusFirstItem] = useState(false);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  const parts = versionParts({
    release: keys.CHANGELOG_VERSION,
    build: keys.FRONTEND_VERSION,
    isSuperAdmin,
  });
  const version = parts.join(VERSION_SEPARATOR);
  const toggleLabel = t("sidebar.documents");
  const infoLabel = t("sidebar.infoPrivacyTerms");

  const resources = [
    {
      href: `${keys.XP_REPORTING_API_URL}/api/supported_devices`,
      label: t("sidebar.supportedDevices"),
      icon: "fa-microchip",
    },
    {
      href: keys.XP_DOCS_URL,
      label: t("sidebar.documentation"),
      icon: "fa-book-open",
    },
    {
      href: keys.XP_CHANGELOG_URL,
      label: t("sidebar.changeLog"),
      icon: "fa-clock-rotate-left",
    },
  ];

  const menuItems = () =>
    Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? []);

  const closeMenu = useCallback((returnFocus = false) => {
    setMenuOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  const openMenu = (withKeyboard) => {
    setFocusFirstItem(withKeyboard);
    setMenuOpen(true);
  };

  // The sidebar scrolls and clips, so the menu is fixed to the viewport, just
  // right of the sidebar's edge and level with the bottom of the toggle.
  useLayoutEffect(() => {
    if (!isMenuOpen) return undefined;
    const place = () => {
      const toggle = toggleRef.current?.getBoundingClientRect();
      const sidebar = toggleRef.current
        ?.closest("#sidenav-main")
        ?.getBoundingClientRect();
      if (!toggle) return;
      setMenuPosition({
        left: (sidebar?.right ?? toggle.right) + 10,
        bottom: Math.max(12, globalThis.innerHeight - toggle.bottom),
      });
    };
    place();
    globalThis.addEventListener("resize", place);
    return () => globalThis.removeEventListener("resize", place);
  }, [isMenuOpen, isCompact]);

  useEffect(() => {
    if (isMenuOpen && focusFirstItem) menuItems()[0]?.focus();
  }, [isMenuOpen, focusFirstItem]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (toggleRef.current?.contains(event.target)) return;
      closeMenu();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isMenuOpen, closeMenu]);

  useEffect(() => {
    if (!isCopied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  const onToggleKeyDown = (event) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      openMenu(true);
    }
  };

  const onMenuKeyDown = (event) => {
    const items = menuItems();
    const index = items.indexOf(document.activeElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
    } else if (event.key === "Tab") {
      // Back on the toggle first, so the Tab carries on from the sidebar
      // rather than from the menu, which is portalled to the end of the page.
      closeMenu(true);
    }
  };

  const openDialog = () => {
    // Focus the toggle before the menu unmounts: the dialog records where to
    // return focus when it opens, and the menu item will be gone by then.
    closeMenu(true);
    setDialogOpen(true);
  };

  const copyVersion = async () => {
    try {
      await navigator.clipboard.writeText(`${PRODUCT_NAME} ${version}`);
      setCopied(true);
    } catch {
      // The browser refused the clipboard (no permission, or not a secure
      // context): show no confirmation rather than claim a copy that failed.
      setCopied(false);
    }
  };

  return (
    <div className="sidebar-info">
      {version && (
        <div
          className="sidebar-info-version sidebar-label"
          data-testid="sidebar-version"
        >
          {/* A version stamp: the release on the first line, the build
              (superadmins only) centred under it. */}
          <span className="sidebar-info-version-text">
            <span className="sidebar-info-version-line">
              {t("sidebar.version")} {parts[0]}
            </span>
            {parts[1] && (
              <span className="sidebar-info-version-line sidebar-info-version-build">
                {parts[1]}
              </span>
            )}
          </span>
          {isSuperAdmin && (
            <button
              type="button"
              className="sidebar-info-copy"
              aria-label={t("infoPage.copyVersion")}
              title={t("infoPage.copyVersion")}
              onClick={copyVersion}
            >
              <i
                className={`${isCopied ? "fas fa-check" : "far fa-copy"}`}
                aria-hidden="true"
              />
            </button>
          )}
          <output className="sr-only">
            {isCopied ? t("infoPage.versionCopied") : ""}
          </output>
        </div>
      )}

      <button
        ref={toggleRef}
        type="button"
        className="sidebar-info-toggle"
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        aria-controls={MENU_ID}
        aria-label={toggleLabel}
        title={isCompact ? toggleLabel : undefined}
        onClick={() => (isMenuOpen ? closeMenu() : openMenu(false))}
        onKeyDown={onToggleKeyDown}
      >
        <span className="sidebar-info-toggle-icon" aria-hidden="true">
          <i className="far fa-circle-question" />
        </span>
        <span className="sidebar-info-toggle-label sidebar-label">
          {toggleLabel}
        </span>
        <i
          className="fas fa-chevron-right sidebar-info-chevron sidebar-label"
          aria-hidden="true"
        />
      </button>

      {/* Portalled: on phones the sidebar panel is transformed, which would
          otherwise turn it into the containing block of this fixed menu. */}
      {isMenuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="sidebar-info-menu"
            style={
              menuPosition
                ? {
                    "--info-menu-left": `${menuPosition.left}px`,
                    "--info-menu-bottom": `${menuPosition.bottom}px`,
                  }
                : undefined
            }
          >
            {/* Only menu items, groups and separators inside the menu; the
                version below it is plain text beside it. */}
            <div
              id={MENU_ID}
              role="menu"
              aria-label={toggleLabel}
              tabIndex={-1}
              onKeyDown={onMenuKeyDown}
            >
              <div role="group" aria-label={t("sidebar.resources")}>
                <div className="sidebar-info-menu-group" aria-hidden="true">
                  {t("sidebar.resources")}
                </div>
                {resources.map((resource) => (
                  <a
                    key={resource.href}
                    role="menuitem"
                    className="sidebar-info-item"
                    href={resource.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => closeMenu()}
                  >
                    <i
                      className={`fas ${resource.icon} sidebar-info-item-icon`}
                      aria-hidden="true"
                    />
                    <span className="sidebar-info-item-text">
                      {resource.label}
                    </span>
                    <i
                      className="fas fa-arrow-up-right-from-square sidebar-info-item-end"
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </div>
              <hr className="sidebar-info-menu-divider" />
              <button
                type="button"
                role="menuitem"
                className="sidebar-info-item sidebar-info-item-primary"
                onClick={openDialog}
              >
                <i
                  className="fas fa-circle-info sidebar-info-item-icon"
                  aria-hidden="true"
                />
                <span className="sidebar-info-item-text">
                  {infoLabel}
                  <small>{t("infoPage.menuHint")}</small>
                </span>
                <i
                  className="fas fa-chevron-right sidebar-info-item-end"
                  aria-hidden="true"
                />
              </button>
            </div>
            {version && (
              <div className="sidebar-info-menu-version">
                {t("sidebar.version")} <VersionText parts={parts} />
              </div>
            )}
          </div>,
          document.body,
        )}

      <InfoDialog
        isOpen={isDialogOpen}
        toggle={() => setDialogOpen(false)}
        t={t}
        versionParts={parts}
      />
    </div>
  );
};

SidebarInfoMenu.propTypes = {
  t: PropTypes.func.isRequired,
  isSuperAdmin: PropTypes.bool,
  isCompact: PropTypes.bool,
};

export default SidebarInfoMenu;
